import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get, post } from "@/lib/api";
import { journeyById } from "@/data/canvas";
import { JourneyShell } from "@/components/JourneyShell";
import { StatePill } from "@/components/StatePill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  UserPlus, Send, Bell, Ban, UserCheck, ExternalLink, Inbox, ShieldCheck, ArrowRight, Check, X, Info,
} from "lucide-react";

const journey = journeyById("onboarding");

export default function OnboardingJourney() {
  const [config, setConfig] = useState(null);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ display_name: "", role: "volunteer", delivery_route: "", expiry_date: "", team: "" });
  const [busy, setBusy] = useState(false);

  const loadInvites = () => get("/mill/invitations").then((d) => setInvites(d.invitations));
  const loadRequests = () => get("/mill/requests").then((d) => setRequests(d.requests));
  useEffect(() => { get("/mill/onboarding/config").then(setConfig); loadInvites(); loadRequests(); }, []);

  const createInvite = async () => {
    if (!form.display_name || !form.delivery_route || !form.expiry_date) { toast.error("Complete name, delivery route and expiry."); return; }
    setBusy(true);
    await post("/mill/invitations", form);
    setForm({ display_name: "", role: "volunteer", delivery_route: "", expiry_date: "", team: "" });
    await loadInvites();
    toast.success("Invitation created (synthetic).");
    setBusy(false);
  };

  const inviteAction = async (id, action, body = {}) => {
    try {
      await post(`/mill/invitations/${id}/${action}`, body);
      await loadInvites();
      toast.success(`Done: ${action.replace(/-/g, " ")}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || `Could not ${action.replace(/-/g, " ")}.`);
    }
  };

  const requestAction = async (id, action) => {
    await post(`/mill/requests/${id}/action`, { action });
    await loadRequests();
    toast.success(`Request ${action.replace("_", " ")}.`);
  };

  return (
    <JourneyShell journey={journey}>
      <div className="space-y-8" data-testid="onboarding-admin">
        <div className="rounded-2xl border border-stone-200 bg-mill-alt/40 p-4 text-sm text-mill-muted" data-testid="admin-view-note">
          <b className="text-mill-ink">Administrator & design-review view.</b> This is the fuller controls view. The invited person sees a
          simple, linear pathway. State machine: Draft → Sent → Opened → Redeemed → Induction in progress → Ready for review → Role active → Suspended / expired / revoked.
        </div>
        {/* Roles authority */}
        {config && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="roles-authority">
            {config.roles.map((r) => (
              <div key={r.id} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`role-card-${r.id}`}>
                <h3 className="font-display font-semibold text-mill-ink">{r.label}</h3>
                <p className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-mill-moss">May</p>
                <ul className="mt-1 space-y-1">{r.may.slice(0, 4).map((m) => <li key={m} className="flex gap-1.5 text-xs text-mill-muted"><Check className="mt-0.5 h-3 w-3 shrink-0 text-mill-moss" />{m}</li>)}</ul>
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-[#B45309]">May not</p>
                <ul className="mt-1 space-y-1">{r.may_not.slice(0, 3).map((m) => <li key={m} className="flex gap-1.5 text-xs text-mill-muted"><X className="mt-0.5 h-3 w-3 shrink-0 text-[#B45309]" />{m}</li>)}</ul>
              </div>
            ))}
          </div>
        )}

        {/* Invite a person */}
        <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="invite-form">
          <div className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-mill-moss" /><h3 className="font-display text-lg font-semibold text-mill-ink">Invite a person</h3></div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div><Label className="text-xs text-mill-muted">Display name</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="invite-name" /></div>
            <div>
              <Label className="text-xs text-mill-muted">Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role" className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                <option value="volunteer">Volunteer</option>
                <option value="booking_desk">Booking / Service Administrator</option>
              </select>
            </div>
            <div><Label className="text-xs text-mill-muted">Email / delivery route</Label><Input value={form.delivery_route} onChange={(e) => setForm({ ...form, delivery_route: e.target.value })} data-testid="invite-route" /></div>
            <div><Label className="text-xs text-mill-muted">Expiry date</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="invite-expiry" /></div>
            <div className="sm:col-span-2"><Label className="text-xs text-mill-muted">Team / group (optional)</Label><Input value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} data-testid="invite-team" /></div>
          </div>
          <Button className="mt-4 bg-mill-moss hover:bg-mill-moss-dark" onClick={createInvite} disabled={busy} data-testid="invite-create"><Send className="mr-1.5 h-4 w-4" /> Create invitation</Button>
          <p className="mt-3 flex items-start gap-1.5 rounded-xl bg-mill-alt px-3 py-2 text-xs text-mill-muted"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This invitation gives access to the Mill onboarding pathway. It does not itself confer permanent authority.</p>
        </div>

        {/* Invitations list */}
        <div data-testid="invitations-list">
          <h3 className="mb-3 font-display text-lg font-semibold text-mill-ink">Volunteer onboarding</h3>
          <div className="space-y-3">
            {invites.length === 0 && <p className="text-sm text-mill-muted">No invitations yet — create one above.</p>}
            {invites.map((inv) => (
              <div key={inv.id} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`invite-row-${inv.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-mill-ink">{inv.display_name} <span className="text-sm font-normal text-mill-muted">· {inv.role}</span></p>
                    <p className="font-mono text-xs text-mill-muted">{inv.invite_url}</p>
                  </div>
                  <StatePill state={inv.lifecycle || inv.status} />
                </div>

                {inv.progress && (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid={`invite-progress-${inv.id}`}>
                    <Chip on={inv.progress.essential_info_complete}>Workspace info</Chip>
                    <Chip on={inv.progress.induction_complete}>Induction</Chip>
                    <Chip on={inv.progress.policies_acknowledged}>Policies</Chip>
                    <Chip on={inv.progress.role_active}>Role active</Chip>
                    <Chip on={inv.progress.support_requested} tone="info">Support request</Chip>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Link to={`/invite/${inv.token}`} data-testid={`invite-open-${inv.id}`} className="inline-flex items-center gap-1.5 rounded-md bg-katlas-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-katlas-blue-dark">
                    Open as invitee <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                  {inv.lifecycle === "ready_for_review" && (
                    <Button size="sm" className="bg-mill-moss hover:bg-mill-moss-dark" onClick={() => inviteAction(inv.id, "activate", {})} data-testid={`invite-activate-${inv.id}`}><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Activate role</Button>
                  )}
                  {["induction_in_progress", "ready_for_review", "role_active"].includes(inv.lifecycle) && (
                    <Button size="sm" variant="outline" onClick={() => inviteAction(inv.id, "return-for-clarification", { note: "Please review a couple of details." })} data-testid={`invite-return-${inv.id}`}>Return for clarification</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => inviteAction(inv.id, "assist", {})} data-testid={`invite-assist-${inv.id}`}>Offer assisted onboarding</Button>
                  {inv.progress && inv.lifecycle !== "suspended" && (
                    <Button size="sm" variant="outline" onClick={() => inviteAction(inv.id, "suspend", {})} data-testid={`invite-suspend-${inv.id}`}>Suspend</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => inviteAction(inv.id, "remind", {})} data-testid={`invite-remind-${inv.id}`}><Bell className="mr-1 h-3.5 w-3.5" /> Remind</Button>
                  <Button size="sm" variant="outline" onClick={() => inviteAction(inv.id, "assign-lead", { lead_name: "Priya Nair" })} data-testid={`invite-assign-${inv.id}`}><UserCheck className="mr-1 h-3.5 w-3.5" /> Assign lead</Button>
                  <Button size="sm" variant="outline" onClick={() => inviteAction(inv.id, "revoke", {})} disabled={inv.status === "revoked"} data-testid={`invite-revoke-${inv.id}`}><Ban className="mr-1 h-3.5 w-3.5" /> Revoke</Button>
                  {inv.team_lead && <span className="text-xs text-mill-muted">Lead: {inv.team_lead}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Service inbox */}
        <div data-testid="service-inbox">
          <div className="mb-3 flex items-center gap-2"><Inbox className="h-5 w-5 text-mill-moss" /><h3 className="font-display text-lg font-semibold text-mill-ink">Service inbox — structured requests</h3></div>
          <div className="space-y-3">
            {requests.length === 0 && <p className="text-sm text-mill-muted">No requests yet. Redeem an invitation and start one as the invitee.</p>}
            {requests.map((r) => (
              <div key={r.id} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`request-row-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-mill-ink">{r.from_display} → {r.to_role_label}</p>
                    <p className="text-xs text-mill-muted">{r.purpose}</p>
                  </div>
                  <StatePill state={r.status} />
                </div>
                {r.selection && <p className="mt-2 rounded-lg bg-mill-alt px-3 py-1.5 text-xs text-mill-ink">“{r.selection}”</p>}
                {r.fields && Object.keys(r.fields).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-mill-muted">
                    {Object.entries(r.fields).map(([k, v]) => <span key={k}><b className="capitalize">{k.replace(/_/g, " ")}:</b> {String(v)}</span>)}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {[["need_info", "Need info"], ["review", "In review"], ["approve", "Approve"], ["decline", "Decline"], ["complete", "Complete"], ["escalate", "Escalate to human"]].map(([a, label]) => (
                    <button key={a} onClick={() => requestAction(r.id, a)} data-testid={`request-${a}-${r.id}`} className="rounded-md border border-stone-300 bg-white px-2.5 py-1 text-xs font-medium text-mill-ink hover:bg-mill-alt">{label}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Future KATLAS mapping */}
        {config && (
          <div className="rounded-2xl border border-dashed border-katlas-blue/40 bg-white p-6" data-testid="future-mapping">
            <div className="mb-4 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-katlas-blue" /><h3 className="font-display text-lg font-semibold text-mill-ink">How this maps to a future KATLAS pilot</h3></div>
            <div className="overflow-hidden rounded-xl border border-stone-200">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-2 bg-mill-alt px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-mill-muted">
                <span>Sandbox element (now)</span><span /><span>Future pilot equivalent</span>
              </div>
              {config.future_mapping.map((m, i) => (
                <div key={i} className={`grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-2.5 text-sm ${i % 2 ? "bg-white" : "bg-mill-alt/30"}`} data-testid={`mapping-row-${i}`}>
                  <span className="text-mill-ink">{m.sandbox}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-katlas-blue" />
                  <span className="text-mill-muted">{m.pilot}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-mill-muted">Synthetic in v0.3. The boundary between this demonstration and a future live KATLAS operation stays visible throughout.</p>
          </div>
        )}
      </div>
    </JourneyShell>
  );
}

function Chip({ on, children, tone }) {
  const cls = on
    ? (tone === "info" ? "bg-[#DBEAFE] text-[#1E40AF]" : "bg-[#D1FAE5] text-[#065F46]")
    : "bg-stone-100 text-stone-500";
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${cls}`}>{on ? <Check className="h-3 w-3" /> : <span className="h-1.5 w-1.5 rounded-full bg-stone-400" />}{children}</span>;
}
